import { useRouter } from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import { localeLanguageCodes, localeNames } from 'shared-locales';
import api from 'api';
import { breakpoints } from 'styles';
import { Button, FlagIcon, LinkButton, User } from 'components/common';
import {
  Dropdown,
  DropdownButton,
  DropdownLink,
  DropdownSelectable
} from 'components/common/dropdown';
import { Icon } from 'components/common/icons';
import { NotificationsCenter } from 'components/common/notifications';
import { latestProductUpdatesDate } from 'components/updates';
import { HeaderGuestLogout } from '.';

import type { Locales } from 'shared-types';

interface Props {
  onHelpClick: () => void;
  showUser: boolean;
}

const HeaderUserNav = ({ onHelpClick, showUser }: Props) => {
  const { dispatch } = useDispatch();

  const { language, messages, serverConfig, theme, user } = useReduxState();

  const router = useRouter();

  const logout = () => {
    // check if sso login redirect has been configured
    let next = '/login';

    if (
      serverConfig.authLoginRedirectStrategy &&
      serverConfig.authStrategies?.find(
        (strategy) => strategy.name === serverConfig.authLoginRedirectStrategy
      )
    ) {
      next += '?action=logout';
    }

    dispatch.logout(next);
  };

  const setLanguage = async (language: Locales) => {
    try {
      const response = await api.get(
        `/admin/server-config/locales?language=${language}`
      );
      const { messages } = response.data;

      dispatch.setLanguage(language, {
        ...messages,
        ...(serverConfig?.customAdminLocales?.[language] || {})
      });

      if (user) {
        try {
          await api.post('/auth/profile', { preferredLanguage: language });
        } catch (err) {}
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const showProductUpdatesNotification =
    user &&
    user.roles.includes('admin') &&
    latestProductUpdatesDate &&
    (!user.productUpdatesLastRead ||
      latestProductUpdatesDate >
        Date.parse(user.productUpdatesLastRead as string));

  const isLoginPage = router.pathname === '/login';

  return (
    <div className="wrapper">
      {user && user.roles.includes('user') && showUser ? (
        <div className="search">
          <LinkButton
            category="transparent"
            customBackgroundColor={theme.headerButtonBackgroundColor}
            customBackgroundColorHover={theme.headerButtonBackgroundColorHover}
            href="/search"
            icon="search"
            iconStyle={{
              color: theme.headerIconColor,
              fontSize: '1.3rem'
            }}
            isRounded={true}
            style={{
              padding: '6px 0 8px',
              height: 30,
              width: 30
            }}
            tooltip={messages.commonSearchAlt}
          />
        </div>
      ) : null}

      {user && showUser ? (
        <div className="help">
          {onHelpClick ? (
            <Button
              category="transparent"
              customBackgroundColor={theme.headerButtonBackgroundColor}
              customBackgroundColorHover={
                theme.headerButtonBackgroundColorHover
              }
              icon="question"
              iconStyle={{
                color: theme.headerIconColor,
                fontSize: '1.3rem'
              }}
              isRounded={true}
              onClick={onHelpClick}
              style={{
                padding: '6px 0 8px',
                height: 30,
                width: 30
              }}
              tooltip={messages.commonHelp}
            />
          ) : (
            <LinkButton
              category="transparent"
              customBackgroundColor={theme.headerButtonBackgroundColor}
              customBackgroundColorHover={
                theme.headerButtonBackgroundColorHover
              }
              href="/help"
              icon="question"
              iconStyle={{
                color: theme.headerIconColor,
                fontSize: '1.3rem'
              }}
              isRounded={true}
              style={{
                padding: '6px 0 8px',
                height: 30,
                width: 30
              }}
              tooltip={messages.commonHelp}
            />
          )}
        </div>
      ) : null}

      {user && user.roles.includes('user') && showUser ? (
        <div className="notifications">
          <NotificationsCenter />
        </div>
      ) : null}

      {serverConfig && serverConfig.supportedLanguages.length > 1 ? (
        <DropdownSelectable
          before={
            <FlagIcon
              lang={localeLanguageCodes[language]}
              style={{ width: '1.8em' }}
            />
          }
          buttonCustomBackgroundColor="transparent"
          buttonCustomBackgroundColorHover={
            theme.headerButtonBackgroundColorHover
          }
          buttonStyle={{
            borderRadius: theme.buttonBorderRadius,
            padding: 4,
            margin: '-2px 0',
            height: 'auto',
            width: 'auto'
          }}
          category="transparent"
          defaultValue={language}
          label={`${messages.commonLanguage}: ${localeNames[language]}`}
          onChange={setLanguage}
          options={Object.keys(localeNames)
            .filter((key: Locales) =>
              serverConfig.supportedLanguages.includes(key)
            )
            .sort((a, b) => {
              // Русский is hardcoded as an exception to be treated as the word "Russian"
              // when sorting alphabetically, so that it doesn't go to the bottom of the list
              const nameA =
                localeNames[a] === 'Русский' ? 'Russian' : localeNames[a];
              const nameB =
                localeNames[b] === 'Русский' ? 'Russian' : localeNames[b];

              return nameA.localeCompare(nameB);
            })
            .map((key) => ({
              iconContent: (
                <FlagIcon
                  lang={localeLanguageCodes[key]}
                  style={{ marginTop: -2 }}
                />
              ),
              text: localeNames[key],
              value: key
            }))}
          showArrow={false}
          tooltip={messages.commonLanguage}
        />
      ) : null}

      {user && showUser ? (
        <div className="user-dropdown">
          <Dropdown
            after={
              <>
                <Icon
                  icon="angle-down"
                  style={{ marginLeft: 6, position: 'relative', top: 2 }}
                />
                {showProductUpdatesNotification ? (
                  <div className="updates-notification" />
                ) : null}
              </>
            }
            before={
              <User
                avatarStyle={{
                  height: 26,
                  width: 26,
                  margin: '-4px 0',
                  padding: '7px 0',
                  position: 'relative',
                  top: -1,
                  left: 1
                }}
                showNameOrUsername={false}
                size="small"
                style={{
                  height: 16,
                  marginRight: 1
                }}
                user={user}
              />
            }
            buttonCustomBackgroundColorHover={
              theme.headerButtonBackgroundColorHover
            }
            buttonStyle={{
              borderRadius: theme.buttonBorderRadius,
              color: theme.headerTextColor,
              fontWeight: 600,
              height: 32,
              padding: '3px 10px 4px 3px'
            }}
            category="transparent"
            styleContentButtons={false}
            title={
              serverConfig.userUseName && user.firstName && user.lastName
                ? `${user.firstName}`
                : user.username
            }
          >
            {user.roles.includes('user') ? (
              <>
                <div className="info">
                  <div className="info-name">
                    {serverConfig.userUseName && user.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user.username}
                  </div>
                  <div className="info-email">{user.email}</div>
                </div>
                <DropdownLink
                  href="/profile"
                  icon="user"
                  text={messages.headerProfile}
                />
                {user.roles.includes('admin') ? (
                  <DropdownLink
                    href="/updates"
                    icon="rocket"
                    showNotification={showProductUpdatesNotification}
                    text={messages.updates}
                  />
                ) : null}
              </>
            ) : null}

            {user.roles.includes('guest') ? (
              <HeaderGuestLogout />
            ) : user.customLogout ? (
              <DropdownLink
                href={user.customLogout}
                icon="sign-out-alt"
                text={messages.headerSignOut}
              />
            ) : (
              <DropdownButton
                icon="sign-out-alt"
                onClick={logout}
                text={messages.headerSignOut}
              />
            )}
          </Dropdown>
        </div>
      ) : !user && !isLoginPage && showUser ? (
        <Button
          category="transparent"
          customBackgroundColor={theme.headerButtonBackgroundColor}
          customBackgroundColorHover={theme.headerButtonBackgroundColorHover}
          customColor={theme.headerTextColor}
          size="header"
          icon="sign-in-alt"
          isRounded={true}
          onClick={() => router.push(`/login?next=${router.asPath}`)}
          style={{ marginLeft: 20 }}
          text={messages.authSignIn}
        />
      ) : null}

      <style jsx>{`
        .wrapper {
          align-items: center;
          display: flex;
          justify-content: flex-end;
        }
        .search {
          margin-right: 10px;
        }
        .help {
          flex-grow: 1;
          margin-right: 10px;
          min-width: 0;
        }
        .notifications {
          display: inline-block;
          margin-right: 25px;
        }
        .user-dropdown {
          display: inline-block;
          margin-left: 20px;
          position: relative;
        }
        .updates-notification {
          background-color: ${theme.dangerColor};
          border-radius: 10px;
          display: inline-block;
          margin: 2px 0 2px 10px;
          position: absolute;
          left: 12px;
          top: 1px;
          height: 6px;
          width: 6px;
        }
        .info {
          border-bottom: 1px solid ${theme.lightGreyDark};
          margin-bottom: 8px;
          max-width: 180px;
          padding: 4px 15px 12px;
          text-align: left;
        }
        .info-name {
          color: ${theme.textColorLight};
          font-size: 1.2rem;
          font-weight: 700;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .info-email {
          color: ${theme.textColorLighter};
          font-size: 1.2rem;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .wrapper :global(.toggle-title) {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default HeaderUserNav;
